/**
 * useHiddenRewards — allergirapporten i Admin-panelet (#219).
 *
 *   - useHiddenRewards()  GET   /api/rewards/admin/hidden-by-allergy (requireAdmin)
 *   - useRenameReward()   PATCH /api/rewards/:id {title}       (requireAdminScope)
 *
 * Serveren bruker samme lag 2-regel som belønningslista til barna, så
 * rapporten viser nøyaktig de belønningene som er skjult for noen. Spørringen
 * er av for andre enn admin (som useAdminCosmetics). Mutasjonen kaster
 * `ApiError`, så seksjonen kan kjenne igjen en utløpt adminopplåsing
 * (isAdminScopeError) og åpne AdminReauthModal.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AllergyHiddenRewardsResponseSchema, type AllergyHiddenReward } from '@stell/shared';
import { jsonInit, sendJson } from '../../lib/api';
import { useSession } from '../auth/useSession';

export const HIDDEN_REWARDS_KEY = ['rewards', 'hidden-by-allergy'] as const;

export function useHiddenRewards() {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  return useQuery<AllergyHiddenReward[]>({
    queryKey: HIDDEN_REWARDS_KEY,
    enabled: isAdmin,
    queryFn: async () =>
      AllergyHiddenRewardsResponseSchema.parse(await sendJson<unknown>('/api/rewards/admin/hidden-by-allergy'))
        .rewards,
  });
}

/**
 * Ny tittel på en belønning. Lag 1 kjører igjen på serveren: en tittel som
 * fortsatt treffer noens allergi gir 422 `reward_authoring_blocked`.
 */
export function useRenameReward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      sendJson<unknown>(`/api/rewards/${encodeURIComponent(id)}`, jsonInit('PATCH', { title })),
    // ['rewards'] dekker rapporten, adminkatalogen og barnas lister.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rewards'] }),
  });
}
